/**
 * swms_hazards_controller.js — trade-aware hazard picker (Stimulus).
 *
 * The hazard catalogue covers every trade, so showing all ~30 options at once
 * buries the handful a given crew needs. This controller keeps a SHORT primary
 * list — the ticked hazards plus the ones most relevant to the chosen trade, up
 * to PRIMARY_LIMIT — and moves the remainder into a Bootstrap collapse that the
 * "Show N more" button reveals with the same height animation the dashboard
 * uses. A search box overrides the split and matches across the whole catalogue.
 *
 * Why a fixed cap rather than "trade-relevant vs not": the catalogue tags most
 * hazards for the catch-all "general" trade, so a relevance-only split barely
 * shortened the list. Relevance now only decides *ordering*; the cap decides
 * *how many* show before "Show more".
 *
 * Favourites & custom hazards (per-tenant, persisted server-side):
 *   A star button on each option POSTs to data-favourite-url; starred hazards
 *   sort to the top and are always kept in the primary list. The "Add your own
 *   hazard" form POSTs hazard/risk/control to data-custom-url, which returns the
 *   rendered option partial this controller inserts (checked) into the picker.
 *   Both keep the page's catalogue in step with the saved library, so the same
 *   custom hazard and stars reappear on the next SWMS.
 *
 * Targets:
 *   trade        — <select> of trade slugs; drives which hazards are "relevant".
 *   search       — <input type=search>; non-empty switches to search mode.
 *   item         — each hazard option <div> (data-order, data-trades, data-text,
 *                  data-favourite, data-key).
 *   primary      — the always-visible grid.
 *   extra        — the Bootstrap .collapse wrapper holding the overflow grid.
 *   extraGrid    — the grid inside the collapse the overflow items move into.
 *   toggle       — the "Show N more / Show fewer" button.
 *   customToggle — the "Add your own hazard" disclosure button.
 *   customForm   — the inline custom-hazard input group.
 *   customHazard/customRisk/customControl — the custom-hazard inputs
 *                  (customControl is one control measure per line).
 *   customRiskBefore/customRiskAfter — optional before/after residual risk
 *                  rating <select>s (Low/Med/High).
 *   customError  — inline error line for the custom-hazard POST.
 *
 * Actions:
 *   tradeChanged()    — re-partition when the trade changes.
 *   filter()          — re-render on each search keystroke.
 *   toggleOther()     — animate the collapse open/closed via Bootstrap.
 *   toggleFavourite() — star/unstar a hazard (POST) and re-sort.
 *   showCustomForm()  — reveal the inline custom-hazard inputs.
 *   addCustom()       — POST a new custom hazard and insert the returned option.
 *   customKeydown()   — Enter in a custom input adds the hazard (never submits
 *                       the surrounding SWMS form).
 *
 * A ticked or starred hazard is always kept in the primary list, so a re-render
 * never hides the crew's selection. No persistent global state — the cached
 * Turbo snapshot re-partitions cleanly on connect().
 */

import { Controller } from "@hotwired/stimulus";

    // Hazards shown before "Show more" — short enough to scan, the rest one tap
    // away. Two desktop columns, so a multiple of 2 reads evenly.
    var PRIMARY_LIMIT = 6;

export default class extends Controller {
        static get targets() {
            return [
                'trade', 'search', 'item', 'primary', 'extra', 'extraGrid', 'toggle',
                'customToggle', 'customForm', 'customHazard', 'customRisk', 'customControl',
                'customRiskBefore', 'customRiskAfter', 'customError',
            ];
        }

        connect() {
            this._extraCount = 0;
            // Bootstrap fires these on the collapse element; keep the label in sync
            // with the animation rather than guessing state at click time.
            this._onShown = () => this._setToggleLabel(true);
            this._onHidden = () => this._setToggleLabel(false);
            if (this.hasExtraTarget) {
                this.extraTarget.addEventListener('shown.bs.collapse', this._onShown);
                this.extraTarget.addEventListener('hidden.bs.collapse', this._onHidden);
            }
            this._render();
            // Set last: itemTargetConnected fires once per existing option as the
            // controller connects, and we don't want a re-render storm before the
            // initial _render() above. After this, an inserted custom hazard does
            // trigger a re-render.
            this._connected = true;
        }

        disconnect() {
            this._connected = false;
            if (this.hasExtraTarget) {
                this.extraTarget.removeEventListener('shown.bs.collapse', this._onShown);
                this.extraTarget.removeEventListener('hidden.bs.collapse', this._onHidden);
            }
        }

        // A custom hazard inserted after connect re-partitions so it lands in the
        // (checked → always-primary) set immediately. Guarded so the per-option
        // connect calls during initial bootstrap don't each trigger a render.
        itemTargetConnected() {
            if (this._connected) this._render();
        }

        tradeChanged() {
            this._collapse();
            this._render();
        }

        filter() {
            this._render();
        }

        toggleOther() {
            var collapse = this._collapseInstance();
            if (collapse) {
                collapse.toggle();              // Bootstrap animates; events set the label.
            } else {
                // No Bootstrap JS available — instant, unanimated fallback.
                var open = this.extraTarget.classList.toggle('show');
                this._setToggleLabel(open);
            }
        }

        // -- favourites ------------------------------------------------------

        toggleFavourite(event) {
            event.preventDefault();
            var button = event.currentTarget;
            var option = button.closest('[data-key]');
            if (!option) return;
            var url = this.element.dataset.favouriteUrl;
            if (!url) return;

            button.disabled = true;
            this._post(url, { key: option.dataset.key })
                .then((res) => res.json())
                .then((data) => {
                    if (typeof data.favourite !== 'boolean') return;
                    this._applyFavouriteState(option, data.favourite);
                    this._collapse();
                    this._render();
                })
                .catch(() => { /* leave state unchanged on failure */ })
                .then(() => { button.disabled = false; });
        }

        _applyFavouriteState(option, favourite) {
            option.dataset.favourite = favourite ? 'true' : 'false';
            var button = option.querySelector('.swms-hazard-star');
            if (button) {
                button.classList.toggle('is-favourite', favourite);
                button.setAttribute('aria-pressed', favourite ? 'true' : 'false');
                var icon = button.querySelector('i');
                if (icon) {
                    icon.classList.toggle('bi-star-fill', favourite);
                    icon.classList.toggle('bi-star', !favourite);
                }
            }
        }

        // -- custom hazards --------------------------------------------------

        showCustomForm() {
            if (!this.hasCustomFormTarget) return;
            this._setHidden(this.customFormTarget, false);
            if (this.hasCustomToggleTarget) this._setHidden(this.customToggleTarget, true);
            if (this.hasCustomHazardTarget) this.customHazardTarget.focus();
        }

        customKeydown(event) {
            // Enter inside an input would submit the surrounding SWMS form; route
            // it to the add action instead. Shift+Enter in the textarea still
            // inserts a newline.
            if (event.key === 'Enter' && !(event.shiftKey && event.target.tagName === 'TEXTAREA')) {
                event.preventDefault();
                this.addCustom();
            }
        }

        addCustom() {
            var url = this.element.dataset.customUrl;
            if (!url || !this.hasCustomHazardTarget) return;

            var payload = {
                hazard: this.customHazardTarget.value.trim(),
                risk: this.hasCustomRiskTarget ? this.customRiskTarget.value.trim() : '',
                // Posted as the raw textarea value (one control per line); the
                // schema splits it into a list server-side.
                controls: this.hasCustomControlTarget ? this.customControlTarget.value.trim() : '',
                risk_before: this.hasCustomRiskBeforeTarget ? this.customRiskBeforeTarget.value : '',
                risk_after: this.hasCustomRiskAfterTarget ? this.customRiskAfterTarget.value : '',
            };
            if (!payload.hazard || !payload.risk || !payload.controls) {
                this._showCustomError('Add a hazard, its risk, and a control measure.');
                return;
            }
            this._showCustomError('');

            this._post(url, payload)
                .then((res) => res.text().then((body) => {
                    if (!res.ok) return Promise.reject(body || 'Could not add hazard.');
                    return body;
                }))
                .then((html) => {
                    // Insert into the primary grid: the new option renders checked,
                    // so itemTargetConnected re-partitions it straight into primary.
                    this.primaryTarget.insertAdjacentHTML('beforeend', html.trim());
                    this._resetCustomForm();
                })
                .catch((message) => {
                    this._showCustomError(typeof message === 'string' ? message : 'Could not add hazard.');
                });
        }

        _resetCustomForm() {
            this.customHazardTarget.value = '';
            if (this.hasCustomRiskTarget) this.customRiskTarget.value = '';
            if (this.hasCustomControlTarget) this.customControlTarget.value = '';
            if (this.hasCustomRiskBeforeTarget) this.customRiskBeforeTarget.value = '';
            if (this.hasCustomRiskAfterTarget) this.customRiskAfterTarget.value = '';
            this._showCustomError('');
            if (this.hasCustomFormTarget) this._setHidden(this.customFormTarget, true);
            if (this.hasCustomToggleTarget) this._setHidden(this.customToggleTarget, false);
        }

        _showCustomError(message) {
            if (!this.hasCustomErrorTarget) return;
            this.customErrorTarget.textContent = message;
            this._setHidden(this.customErrorTarget, !message);
        }

        // POST as form-encoded so Flask-WTF reads the CSRF token from the field;
        // data-csrf carries the token rendered into the controller root.
        _post(url, fields) {
            var body = new URLSearchParams(fields);
            body.set('csrf_token', this.element.dataset.csrf || '');
            return fetch(url, {
                method: 'POST',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
                body: body,
            });
        }

        // -- rendering -------------------------------------------------------

        _render() {
            var query = (this.hasSearchTarget ? this.searchTarget.value : '').trim().toLowerCase();
            var items = this._orderedItems();

            if (query) {
                // Search mode: pull everything back into the primary grid, filter by
                // text, and drop the "more" affordance entirely.
                this._collapse();
                items.forEach((item) => {
                    this.primaryTarget.appendChild(item);
                    this._setHidden(item, item.dataset.text.indexOf(query) === -1);
                    item.classList.remove('swms-hazard-option--other');
                });
                this._setHidden(this.toggleTarget, true);
                return;
            }

            var trade = this.hasTradeTarget ? this.tradeTarget.value : '';
            var primary = this._choosePrimary(items, trade);

            items.forEach((item) => {
                this._setHidden(item, false);
                if (primary.has(item)) {
                    this.primaryTarget.appendChild(item);
                    item.classList.remove('swms-hazard-option--other');
                } else {
                    this.extraGridTarget.appendChild(item);
                    item.classList.add('swms-hazard-option--other');
                }
            });

            this._setToggle(items.length - primary.size);
        }

        // The primary set: every starred or ticked hazard (never hide a choice or
        // a favourite), then the trade-relevant ones, then a top-up of the rest —
        // capped at PRIMARY_LIMIT unless favourites/ticks alone already exceed it.
        _choosePrimary(items, trade) {
            var primary = new Set();
            items.forEach((item) => {
                if (this._isFavourite(item) || this._isChecked(item)) primary.add(item);
            });
            items.forEach((item) => {
                if (primary.size >= PRIMARY_LIMIT || primary.has(item)) return;
                if (this._trades(item).indexOf(trade) !== -1) primary.add(item);
            });
            items.forEach((item) => {
                if (primary.size >= PRIMARY_LIMIT || primary.has(item)) return;
                primary.add(item);
            });
            return primary;
        }

        _setToggle(extraCount) {
            if (!this.hasToggleTarget) return;
            this._extraCount = extraCount;
            if (extraCount <= 0) {
                this._setHidden(this.toggleTarget, true);
                return;
            }
            this._setHidden(this.toggleTarget, false);
            this._setToggleLabel(this._isExpanded());
        }

        _setToggleLabel(expanded) {
            if (!this.hasToggleTarget) return;
            this.toggleTarget.setAttribute('aria-expanded', expanded ? 'true' : 'false');
            this.toggleTarget.textContent = expanded
                ? 'Show fewer hazards'
                : 'Show ' + this._extraCount + ' more hazard' + (this._extraCount === 1 ? '' : 's');
        }

        _isExpanded() {
            return this.hasExtraTarget && this.extraTarget.classList.contains('show');
        }

        _collapse() {
            if (!this._isExpanded()) return;
            var collapse = this._collapseInstance();
            if (collapse) {
                collapse.hide();
            } else {
                this.extraTarget.classList.remove('show');
            }
        }

        _collapseInstance() {
            var bs = window.bootstrap;
            if (!bs || !bs.Collapse || !this.hasExtraTarget) return null;
            return bs.Collapse.getOrCreateInstance(this.extraTarget, { toggle: false });
        }

        // Items ordered favourites-first, then catalogue order, regardless of which
        // grid they currently sit in — so stars float to the top and options keep a
        // stable position across trade changes and Turbo restores.
        _orderedItems() {
            return this.itemTargets.slice().sort((a, b) => {
                var fa = this._isFavourite(a) ? 0 : 1;
                var fb = this._isFavourite(b) ? 0 : 1;
                if (fa !== fb) return fa - fb;
                return (parseInt(a.dataset.order, 10) || 0) - (parseInt(b.dataset.order, 10) || 0);
            });
        }

        // Hide/show via a class, not the `hidden` property: option labels carry
        // Bootstrap's .d-flex and the toggle carries .btn, both display:...!important
        // which override the UA [hidden] rule. The swms-hazard-hidden rule (swms.css,
        // loaded after Bootstrap) is !important and wins.
        _setHidden(el, hidden) {
            el.classList.toggle('swms-hazard-hidden', hidden);
        }

        _trades(item) {
            return item.dataset.trades ? item.dataset.trades.split(' ') : [];
        }

        _isFavourite(item) {
            return item.dataset.favourite === 'true';
        }

        _isChecked(item) {
            var box = item.querySelector('input[type="checkbox"]');
            return box ? box.checked : false;
        }
}
